/* ==========================================================================
   AML product page behaviour - 1.21 "Wednesday Fixes" (2026-09-09)
   A3. Sticky mobile buy bar.

   2.24 (2026-09-16, audit finding 11): on a poster page the bar also names the
   edition the picker is on, so a shopper who has scrolled past the picker can
   see what the button will add. It shares the price line rather than taking a
   line of its own, so the bar is exactly as tall as it was and still clears the
   picker. Only pages that carry the editions picker ([data-aml-ed]) get it, so
   candle and other product pages are untouched.

   Mirrors Opal's real Add to cart button (label, disabled state) and its live
   price element, and shows a fixed bottom bar only while the real button is
   off screen, so there are never two buy buttons visible at once.

   NOTE: Opal binds its ajax add-to-cart to a *click* on [data-product-atc]
   (see ProductDetails / StaticProduct in editions.js), not to the form's
   submit event. So the bar forwards a click to the real button rather than
   calling form.requestSubmit(), which would bypass Opal's handler and do a
   full-page form POST.
   ========================================================================== */
(function () {
  'use strict';

  const MOBILE = '(max-width: 767px)';

  let bar = null;
  let titleEl = null;
  let editionEl = null;
  let priceEl = null;
  let editionSelect = null;
  let buttonEl = null;
  let realButton = null;
  let realPrice = null;
  let buttonObserver = null;
  let priceObserver = null;
  let intersectionObserver = null;

  function findRealButton() {
    const form = document.querySelector('form[data-product-form]');
    if (!form) return null;
    // 2.26 "Sales Paused": no buy bar on a poster whose button is hidden (snippets/product-form.liquid).
    if (form.querySelector('[data-aml-paused]')) return null;
    return form.querySelector('[type="submit"]');
  }

  function productTitle() {
    const el = document.querySelector(
      '.product-details h1.product__heading, h1.product__heading, .product-details h1, [data-product-title]'
    );
    return el ? el.textContent.trim() : '';
  }

  function buttonLabel() {
    if (!realButton) return '';
    const text = realButton.querySelector('[data-product-atc-text]');
    return (text ? text.textContent : realButton.textContent).trim();
  }

  function syncButton() {
    if (!buttonEl || !realButton) return;
    buttonEl.textContent = buttonLabel();
    buttonEl.disabled = realButton.disabled;
  }

  function syncPrice() {
    if (!priceEl) return;
    priceEl.textContent = realPrice ? realPrice.textContent.trim() : '';
  }

  /* The picker drives Opal's hidden option <select> (assets/aml-editions.js), so its
     value is the edition's own label - "#29 of 50", or "misprint". Reading the select
     rather than the picker markup means the bar is right on first paint too, because
     Liquid marks the selected option server-side. */
  function findEditionSelect() {
    if (!document.querySelector('[data-aml-ed]')) return null;
    return document.querySelector('.product-details__options select[data-product-option]');
  }

  function syncEdition() {
    if (!editionEl) return;
    const value = editionSelect ? editionSelect.value.trim() : '';
    editionEl.textContent = value;
    editionEl.hidden = !value;
  }

  /* 2.35, Dan: "the sticky add to cart has a subtle animation on enter and exit, and only appears after you see the
     add to cart for the first time on that product page". The bar now slides and fades in and out (aml-product.css,
     .is-shown) instead of jumping, and stays away until the real button has been on screen once; after that it
     behaves as before: shown whenever the real button is off screen. */
  let seenButton = false;
  let hideTimer = null;

  function setShown(show) {
    if (!bar) return;
    clearTimeout(hideTimer);
    if (show) {
      bar.hidden = false;
      void bar.offsetHeight;                      // commit the hidden pose so the slide-in runs
      bar.classList.add('is-shown');
      document.body.style.paddingBottom = bar.offsetHeight + 'px';
    } else {
      bar.classList.remove('is-shown');
      document.body.style.paddingBottom = '';
      hideTimer = setTimeout(function () { if (bar && !bar.classList.contains('is-shown')) bar.hidden = true; }, 320);
    }
  }

  function build() {
    if (bar) return;

    realButton = findRealButton();
    if (!realButton) return;

    realPrice = document.querySelector('[data-product-price]');

    bar = document.createElement('div');
    bar.className = 'aml-buybar';
    bar.setAttribute('data-aml-buybar', '');
    bar.hidden = true;

    const text = document.createElement('div');
    text.className = 'aml-buybar__text';

    titleEl = document.createElement('span');
    titleEl.className = 'aml-buybar__title';
    titleEl.textContent = productTitle();

    const meta = document.createElement('span');
    meta.className = 'aml-buybar__meta';

    editionSelect = findEditionSelect();

    editionEl = document.createElement('span');
    editionEl.className = 'aml-buybar__edition';
    editionEl.hidden = true;

    priceEl = document.createElement('span');
    priceEl.className = 'aml-buybar__price';

    meta.appendChild(editionEl);
    meta.appendChild(priceEl);

    text.appendChild(titleEl);
    text.appendChild(meta);

    buttonEl = document.createElement('button');
    buttonEl.type = 'button';
    /* Opal's own primary product button class, so the bar matches the theme. */
    buttonEl.className = 'aml-buybar__button product-details__add-to-cart-button';

    bar.appendChild(text);
    bar.appendChild(buttonEl);
    document.body.appendChild(bar);

    syncButton();
    syncPrice();
    syncEdition();

    buttonEl.addEventListener('click', onBarClick);

    if (editionSelect) editionSelect.addEventListener('change', syncEdition);

    buttonObserver = new MutationObserver(syncButton);
    buttonObserver.observe(realButton, {
      childList: true,
      characterData: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled'],
    });

    if (realPrice) {
      priceObserver = new MutationObserver(syncPrice);
      priceObserver.observe(realPrice, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    }

    intersectionObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) seenButton = true;
          setShown(seenButton && !entry.isIntersecting);
        });
      },
      { threshold: 0 }
    );
    intersectionObserver.observe(realButton);
  }

  function onBarClick() {
    if (!realButton || realButton.disabled) return;
    realButton.click();
  }

  function destroy() {
    if (buttonObserver) { buttonObserver.disconnect(); buttonObserver = null; }
    if (priceObserver) { priceObserver.disconnect(); priceObserver = null; }
    if (intersectionObserver) { intersectionObserver.disconnect(); intersectionObserver = null; }
    if (buttonEl) buttonEl.removeEventListener('click', onBarClick);
    if (editionSelect) editionSelect.removeEventListener('change', syncEdition);
    if (bar && bar.parentNode) bar.parentNode.removeChild(bar);
    bar = null;
    titleEl = null;
    editionEl = null;
    editionSelect = null;
    priceEl = null;
    buttonEl = null;
    realButton = null;
    realPrice = null;
    document.body.style.paddingBottom = '';
    clearTimeout(hideTimer);
  }

  function init() {
    /* Never in the theme editor. */
    if (window.Shopify && window.Shopify.designMode) return;
    if (!('IntersectionObserver' in window) || !('MutationObserver' in window)) return;

    const mq = window.matchMedia(MOBILE);

    const apply = function () {
      if (mq.matches) {
        build();
      } else {
        destroy();
      }
    };

    apply();

    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', apply);
    } else if (typeof mq.addListener === 'function') {
      mq.addListener(apply);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* Candle PDP (1.31): show only the selected scent's image plus the two constant images, swap the scent copy,
   fold the description into an accordion. Active only where the aml-scent block renders (product.candle template). */
(function () {
  const root = document.querySelector('[data-aml-scent]');
  const dataEl = document.querySelector('[data-aml-scent-data]');
  if (!root || !dataEl) return;
  let data; try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
  /* WHETHER to scope the gallery, and WHAT the gallery may then show, are two separate questions.

     WHETHER (2.13): the old test was "the product declares constant media", which broke twice over -
     that metafield can be empty, and if the test went false scoping stopped and all 23 scent photos
     appeared on every scent's page. So the test is now the product's own shape, taken from the blob:
     its variants ARE scents (more than one carries per-variant scent copy) AND they genuinely declare
     different media from each other. The candle bundles fail the first half - they carry no
     per-variant scent copy at all, even the golden hour bundle whose four variants do have four
     different featured images - so every bundle keeps its whole gallery. Anything off the candle
     template never reaches this code: the blob only exists on product.candle.

     WHAT: the selected variant's own image plus `aml.pdp_constant_media`, which means extra images
     allowed in the gallery alongside it - the outdoor candle's lifestyle hero is a real product view
     and belongs there. Process imagery is a different job and a different metafield
     (`aml.process_media`, rendered below the buy module by snippets/aml-process-figure.liquid).
     On the 1-wick that list is empty, so its gallery is the one scent photo and nothing else. */
  const constants = (data.constants || []).map(Number).filter((n) => n);
  const variantList = Object.values(data.variants || {});
  const withCopy = variantList.filter((v) => v.copy && String(v.copy).trim()).length;
  const distinctMedia = new Set(variantList.map((v) => Number(v.media)).filter((n) => n)).size;
  const perVariantGallery = withCopy > 1 && distinctMedia > 1;
  const nameEl = root.querySelector('[data-aml-scent-name]');
  const copyEl = root.querySelector('[data-aml-scent-copy]');
  const form = root.closest('form') || document.querySelector('form[data-product-form]') || document;
  const masterSelect = () => document.querySelector('select[data-variants], select[name="id"]');
  // 2.17: the scent is the option that is not `size` (Opal's label carries the option name), so the name line reads
  // "bugger off", never the two-option variant title "bugger off / 14 oz".
  const optName = (s) => { const w = s.closest('.product-details__option-wrapper'); const l = w && w.querySelector('.product-details__option-name'); return l ? l.textContent.trim().toLowerCase() : ''; };
  const scentSel = [...document.querySelectorAll('.product-details__options select[data-product-option]')].find((s) => optName(s) !== 'size');
  const scentIdx = scentSel ? Number(scentSel.getAttribute('data-product-option')) || 0 : 0;

  function currentVariantId() {
    const sel = masterSelect();
    if (sel && data.variants[sel.value]) return sel.value;
    // Fallback: match the option selects' values against each variant's option list.
    const picks = [...document.querySelectorAll('.product-details__options select:not([name="id"])')].map((s) => s.value);
    if (picks.length) { const hit = Object.entries(data.variants).find(([, v]) => JSON.stringify(v.options) === JSON.stringify(picks)); if (hit) return hit[0]; }
    return Object.keys(data.variants)[0];
  }
  const scentOf = (v) => (v.options && v.options[scentIdx]) || v.title;
  const leadMedia = (v) => { const first = variantList.find((w) => w.media && scentOf(w) === scentOf(v)); return Number((first || v).media); };
  function scope(variantId) {
    const v = data.variants[variantId]; if (!v) return;
    // Candle singles: the gallery is the selected scent's own image plus any extra gallery images
    // the product allows through `aml.pdp_constant_media`.
    if (perVariantGallery && v.media) {
      // 2.42, Dan: "if you switch to the 14 ounce, the 8 ounce photo is still there ... ensure that the 14 ounce can's
      // product photo is before the specifications". Reverses 2.34 (which kept the jar on show for every size): the
      // gallery is now the selected size's own photo, first, then the constant images (the spec drawing). Opal
      // already opens the variant's featured image on a size change. The thumbnail is moved, not restyled: Opal's
      // gallery selects by the data-product-gallery-thumbnail index and binds each thumbnail itself, so its position
      // in the row is free to change.
      const own = Number(v.media);
      const allowed = new Set([own, ...constants]);
      document.querySelectorAll('[data-product-gallery-figure][data-media], [data-product-gallery-thumbnail][data-media]').forEach((el) => {
        el.classList.toggle('aml-hidden', !allowed.has(Number(el.getAttribute('data-media'))));
      });
      const th = document.querySelector('[data-product-gallery-thumbnail][data-media="' + own + '"]');
      if (th && th.parentNode && th.parentNode.firstElementChild !== th) th.parentNode.insertBefore(th, th.parentNode.firstElementChild);
      const show = () => { if (last !== String(variantId)) return; const fig = document.querySelector('[data-product-gallery-figure][data-media="' + own + '"]'); if (fig && th && fig.getAttribute('data-product-gallery-selected') !== 'true') th.click(); };
      show(); setTimeout(show, 60); setTimeout(show, 300);
    }
    if (nameEl) nameEl.textContent = (v.options && v.options[scentIdx]) || v.title;
    if (copyEl) { copyEl.textContent = v.copy || ''; if (root) root.hidden = !(v.copy && v.copy.trim()); }
    // Single announcement of "the selected variant changed". The scent atmosphere (2.13) listens
    // here instead of re-deriving the variant from Opal's selects a second time.
    document.dispatchEvent(new CustomEvent('aml:variant', { detail: { id: String(variantId) } }));
  }
  let last = null;
  const sync = () => { const id = currentVariantId(); if (id !== last) { last = id; scope(id); } };
  form.addEventListener('change', () => { setTimeout(sync, 0); setTimeout(sync, 150); });
  const sel = masterSelect();
  if (sel && 'MutationObserver' in window) new MutationObserver(sync).observe(sel, { attributes: true, childList: true, subtree: true });
  const gallery = document.querySelector('[data-product-gallery]');
  if (gallery && 'MutationObserver' in window) new MutationObserver(sync).observe(gallery, { attributes: true, subtree: true, attributeFilter: ['data-product-gallery-selected'] });
  sync();

  /* 2.34, Dan: "try to improve loading times for candle images". The other scents' photos are lazy images in
     hidden gallery slots, so each one was only requested after its chip was picked. Now: the scent under the
     pointer is fetched at once, and once the page has loaded and the browser is idle the rest follow one at a
     time (about 40-60 KB each), so a pick shows a photo that is already there. Skipped on data-saver and slow
     connections. */
  if (perVariantGallery) {
    const figImg = (media) => document.querySelector('[data-product-gallery-figure][data-media="' + media + '"] img');
    const fetchNow = (img, done) => { if (!img || img.loading !== 'lazy' || (img.complete && img.naturalWidth)) return done && done(); if (done) { img.addEventListener('load', done, { once: true }); img.addEventListener('error', done, { once: true }); } img.loading = 'eager'; };
    const leads = [...new Set(variantList.filter((w) => w.media).map((w) => Number(w.media)))];   // 2.42: every size's photo, since each size now shows its own
    // Delegated: the scent chips are built further down this file.
    document.addEventListener('pointerover', (e) => { const chip = e.target.closest && e.target.closest('.aml-chip--scent'); if (!chip) return; const w = variantList.find((x) => x.media && scentOf(x) === chip.dataset.scent); if (w) fetchNow(figImg(leadMedia(w))); }, { passive: true });
    const conn = navigator.connection;
    const slow = conn && (conn.saveData || /(^|-)(2g|3g)$/.test(conn.effectiveType || ''));
    const warm = () => { let i = 0; const next = () => { if (i >= leads.length) return; fetchNow(figImg(leads[i++]), next); }; next(); };
    const idle = () => ('requestIdleCallback' in window ? window.requestIdleCallback(warm, { timeout: 5000 }) : setTimeout(warm, 2500));
    if (!slow) { if (document.readyState === 'complete') idle(); else window.addEventListener('load', idle, { once: true }); }
  }

  // Description accordion, animated via grid-template-rows (no fixed heights).
  // 2.17, Dan: "details & specifications open by default" - at every width, phones included (it used to start closed below 960px).
  const desc = document.querySelector('.product-details__description');
  if (desc && !desc.closest('.aml-acc')) {
    const acc = document.createElement('div'); acc.className = 'aml-acc';
    const btn = document.createElement('button'); btn.type = 'button'; btn.className = 'aml-acc__toggle'; btn.textContent = 'details & specifications'; btn.id = 'aml-acc-toggle';
    const body = document.createElement('div'); body.className = 'aml-acc__body'; body.id = 'aml-acc-body'; body.setAttribute('role', 'region'); body.setAttribute('aria-labelledby', btn.id);
    const inner = document.createElement('div'); inner.className = 'aml-acc__inner';
    desc.parentNode.insertBefore(acc, desc); acc.appendChild(btn); acc.appendChild(body); body.appendChild(inner); inner.appendChild(desc);
    const set = (open) => { acc.classList.toggle('is-open', open); btn.setAttribute('aria-expanded', String(open)); body.setAttribute('aria-hidden', String(!open)); };
    btn.setAttribute('aria-controls', body.id);
    set(true);
    btn.addEventListener('click', () => set(!acc.classList.contains('is-open')));
  }
})();

/* Candle PDP (1.32): grouped scent chips replace the visible dropdown. Opal's <select> stays in the DOM (hidden) and is
   the source of truth: a chip click sets it and fires change, so Opal's variant/price/gallery logic and the scent block run as before. */
(function () {
  const dataEl = document.querySelector('[data-aml-scent-data]'); if (!dataEl) return;
  let data; try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
  // 2.17: options are told apart by name (Opal's label), not position: `size` gets its own chip row, the scent is the other one.
  const optName = (s) => { const w = s.closest('.product-details__option-wrapper'); const l = w && w.querySelector('.product-details__option-name'); return l ? l.textContent.trim().toLowerCase() : ''; };
  const optionSelects = [...document.querySelectorAll('.product-details__options select[data-product-option]')];
  const sizeSelect = optionSelects.find((s) => optName(s) === 'size') || null;
  const optionSelect = optionSelects.find((s) => s !== sizeSelect); if (!optionSelect) return;
  const optionIndex = Number(optionSelect.getAttribute('data-product-option')) || 0;
  const sizeIndex = sizeSelect ? Number(sizeSelect.getAttribute('data-product-option')) || 0 : -1;
  const variants = Object.entries(data.variants).map(([id, v]) => ({ id, ...v }));
  if (!variants.some((v) => v.family)) return;                                   // families not set: leave the dropdown alone
  const families = []; variants.forEach((v) => { if (v.family && !families.includes(v.family)) families.push(v.family); });
  const scentOf = (v) => (v.options && v.options[optionIndex]) || v.title;
  const sizeOf = (v) => v.options && v.options[sizeIndex];
  // The sizes that exist for a scent, in the product's option order (the order Opal renders the size select in).
  const sizesOf = (scent) => sizeSelect ? [...sizeSelect.options].map((o) => o.value).filter((s) => variants.some((v) => scentOf(v) === scent && sizeOf(v) === s)) : [];
  const master = () => document.querySelector('select[data-variants], select[name="id"]');
  // A ?variant= in the URL (homepage scent cards deep-link here) wins on first load; after that Opal's master select is the source of truth.
  const urlVariant = new URLSearchParams(location.search).get('variant'); let urlUsed = false;
  const currentVariant = () => { if (urlVariant && !urlUsed && data.variants[urlVariant]) return data.variants[urlVariant]; const m = master(); return m && data.variants[m.value]; };
  const currentScent = () => { const v = currentVariant(); return v ? scentOf(v) : optionSelect.value; };
  const currentSize = () => { const v = currentVariant(); return (v && sizeOf(v)) || sizeSelect.value; };

  const wrap = document.createElement('div'); wrap.className = 'aml-picker';
  // 2.43, Dan: '"all" selection shouldn't be in line with the categories, bad ux'. "all" is a reset, not a family, so it
  // leaves the chip row and becomes a quiet text button at the end of the "scent" label line. A family chip pressed a
  // second time also goes back to all.
  wrap.innerHTML = '<div class="aml-picker__head"><p class="aml-picker__label" id="aml-picker-label">scent</p>'
    + '<button type="button" class="aml-picker__all" aria-pressed="false">all scents</button></div>'
    + '<div class="aml-picker__families" role="group" aria-label="scent families"></div>'
    + '<div class="aml-picker__scents" role="radiogroup" aria-labelledby="aml-picker-label"></div>';
  const famRow = wrap.querySelector('.aml-picker__families'), scentRow = wrap.querySelector('.aml-picker__scents');
  const allBtn = wrap.querySelector('.aml-picker__all');
  const COLORS = { 'fresh': '#9fe3c8', 'floral': '#f2a7c3', 'woody & earthy': '#a8b56b', 'fruit & citrus': '#f5d76e', 'sweet & spiced': '#d98a5b' };
  const paint = (el, fam) => { if (COLORS[fam]) el.style.setProperty('--fam', COLORS[fam]); };
  let activeFamily = 'all';
  const textSpan = (t) => { const sp = document.createElement('span'); sp.className = 'aml-chip__text'; sp.textContent = t; return sp; };
  const famBtn = (name) => { const b = document.createElement('button'); b.type = 'button'; b.className = 'aml-chip aml-chip--family'; b.innerHTML = '<span class="aml-chip__text"></span>'; b.firstChild.textContent = name; b.dataset.family = name; b.setAttribute('aria-pressed', 'false'); paint(b, name); b.addEventListener('click', () => { activeFamily = activeFamily === name ? 'all' : name; render(); reveal(); }); return b; };
  allBtn.addEventListener('click', () => { if (activeFamily === 'all') return; activeFamily = 'all'; render(); reveal(); });
  // 2.35, Dan: "put the all scents ... as the very last option instead of the first option, and always have the scent
  // open up in their scent group" - twenty pills at once pushed add to cart far down. "all" moves to the end of the
  // family row and the picker opens on the current scent's family (set just before the first render, below).
  families.forEach((f) => famRow.appendChild(famBtn(f)));
  const seen = new Set();
  variants.forEach((v) => {
    const scent = scentOf(v); if (seen.has(scent)) return; seen.add(scent);
    const label = document.createElement('label'); label.className = 'aml-chip aml-chip--scent'; label.dataset.family = v.family || ''; label.dataset.scent = scent; paint(label, v.family);
    if (variants.filter((x) => scentOf(x) === scent).every((x) => x.available === false)) { label.classList.add('is-soldout'); label.setAttribute('aria-label', scent + ', sold out'); }   // 2.17: sold out only when every size is
    const input = document.createElement('input'); input.type = 'radio'; input.name = 'aml-scent-pick'; input.value = scent; input.className = 'aml-chip__input';
    input.addEventListener('change', () => {
      // 2.17: never ask Opal for a combination that does not exist - move the size first (silently), then change the scent.
      if (sizeSelect) { const sizes = sizesOf(scent); if (sizes.length && !sizes.includes(sizeSelect.value)) sizeSelect.value = sizes[0]; }
      optionSelect.value = scent; optionSelect.dispatchEvent(new Event('change', { bubbles: true }));
    });
    label.appendChild(input); label.appendChild(textSpan(scent)); scentRow.appendChild(label);
  });
  // 2.17: size chips, one per size value; render() shows only the sizes the current scent has and hides the row when that is one.
  let sizeLabel = null, sizeRow = null;
  if (sizeSelect) {
    sizeLabel = document.createElement('p'); sizeLabel.className = 'aml-picker__label aml-picker__label--size'; sizeLabel.id = 'aml-picker-size-label'; sizeLabel.textContent = 'size';
    sizeRow = document.createElement('div'); sizeRow.className = 'aml-picker__sizes'; sizeRow.setAttribute('role', 'radiogroup'); sizeRow.setAttribute('aria-labelledby', sizeLabel.id);
    [...sizeSelect.options].forEach((o) => {
      const size = o.value;
      const label = document.createElement('label'); label.className = 'aml-chip aml-chip--size'; label.dataset.size = size;
      const input = document.createElement('input'); input.type = 'radio'; input.name = 'aml-size-pick'; input.value = size; input.className = 'aml-chip__input';
      input.addEventListener('change', () => {
        // 2.42: size now comes first, so a size can be picked that the current scent does not come in (14 oz is only some
        // scents). Then the scent moves too, silently and first, to the first scent that has this size - in stock and in the
        // same family if there is one - so Opal is never asked for a combination that does not exist.
        const cur = currentScent();
        if (!variants.some((v) => scentOf(v) === cur && sizeOf(v) === size)) {
          const fam = (variants.find((v) => scentOf(v) === cur) || {}).family;
          const pool = variants.filter((v) => sizeOf(v) === size);
          const pick = pool.find((v) => v.available !== false && v.family === fam) || pool.find((v) => v.available !== false) || pool[0];
          if (pick) { optionSelect.value = scentOf(pick); if (pick.family && families.includes(pick.family)) activeFamily = pick.family; }
        }
        sizeSelect.value = size; sizeSelect.dispatchEvent(new Event('change', { bubbles: true }));
        setTimeout(reveal, 0);                                                   // after the re-render queued by that change
        bringPhotoIntoView();
      });
      label.appendChild(input); label.appendChild(textSpan(size)); sizeRow.appendChild(label);
    });
    // 2.42, Dan: "move the size to before the scent". Size leads the picker, so a shopper picks 8 oz or 14 oz first and
    // the scent chips then show which scents come in it.
    wrap.insertBefore(sizeRow, wrap.firstChild); wrap.insertBefore(sizeLabel, sizeRow);
    wrap.classList.add('has-sizes');
  }
  // 2.42, Dan: "a subtle animation when you switch between the variant picker groups". The scent chips that are on show
  // after a family or size switch rise 4 px and fade in, one after another, 18 ms apart - about a quarter second in all.
  // Web Animations only, so nothing is left behind on the chips; off under reduced motion.
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');
  function reveal() {
    if (still.matches || !Element.prototype.animate) return;
    const shown = [...scentRow.querySelectorAll('.aml-chip--scent:not(.aml-hidden)')];
    shown.forEach((el, i) => el.animate(
      [{ opacity: 0, transform: 'translateY(4px)' }, { opacity: 1, transform: 'none' }],
      { duration: 220, delay: Math.min(i, 12) * 18, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)', fill: 'backwards' }
    ));
  }
  // 2.42, Dan: after a size pick "scroll back up to make sure that they see the new 14 ounce can". On phones the photo is
  // above the picker; if less than half of it is on screen, the page glides back to it. Desktop keeps the photo pinned
  // beside the picker, so it is always in view there.
  function bringPhotoIntoView() {
    if (!window.matchMedia('(max-width: 959px)').matches) return;
    const g = document.querySelector('[data-product-gallery]'); if (!g) return;
    const r = g.getBoundingClientRect(); const seen = Math.min(r.bottom, window.innerHeight) - Math.max(r.top, 0);
    if (seen >= r.height / 2) return;
    const head = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--aml-sticky-top')) || 64;
    setTimeout(() => window.scrollTo({ top: Math.max(0, r.top + window.pageYOffset - head), behavior: still.matches ? 'auto' : 'smooth' }), 120);
  }
  // 2.43, Dan: "the fill ... moves behind the card towards a different scent ... a little liquidy". Each chip row has one
  // coloured blob that is the selected fill. On a new pick it stretches from the old chip towards the new one, thins
  // as it travels, lands with a small squash and settles - about 0.45 s. The chips keep their black fill; the blob sits
  // over it and under the chip's text. Reduced motion: the blob just jumps.
  // 2.43 second pass, Dan: "blob animation should exist BEHIND everything; so thus not visible outside of the chips".
  // The blob is drawn once per chip: each chip holds a clipped window (.aml-chip__blob) onto one shared blob that
  // lives in the row's coordinates, so the colour only ever shows inside chip shapes, over the chip's black fill and
  // under its text. Every window plays the same keyframes, offset by its own chip's position.
  const blobs = new Map();
  const boxOf = (c) => ({ x: c.offsetLeft, y: c.offsetTop, w: c.offsetWidth, h: c.offsetHeight });
  const same = (a, b) => a && b && Math.abs(a.x - b.x) < 1 && Math.abs(a.y - b.y) < 1 && Math.abs(a.w - b.w) < 1 && Math.abs(a.h - b.h) < 1;
  const frameIn = (r, c, extra) => Object.assign({ transform: `translate(${r.x - c.x - c.bl}px, ${r.y - c.y - c.bt}px)`, width: r.w + 'px', height: r.h + 'px' }, extra || {});
  function windowOf(chip) {
    let w = chip.querySelector(':scope > .aml-chip__blob');
    if (!w) { w = document.createElement('span'); w.className = 'aml-chip__blob'; w.setAttribute('aria-hidden', 'true'); chip.insertBefore(w, chip.firstChild); }
    return w;
  }
  function syncBlob(row, sel) {
    if (!blobs.has(row)) { blobs.set(row, { chip: null, box: null, color: '' }); row.classList.add('has-blob'); }
    const st = blobs.get(row);
    const chips = [...row.children].filter((c) => c.classList.contains('aml-chip'));
    const chip = row.hidden ? null : row.querySelector(sel);
    if (!chip || chip.offsetParent === null) {
      chips.forEach((c) => { const w = c.querySelector(':scope > .aml-chip__blob'); if (!w) return; w.getAnimations().forEach((a) => a.cancel()); const was = w.style.opacity === '1'; w.style.opacity = '0'; if (was && st.chip && !still.matches) w.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 180, easing: 'ease-in' }); });
      st.chip = null; st.box = null; return;
    }
    const to = boxOf(chip);
    const fam = getComputedStyle(chip).getPropertyValue('--fam').trim() || '#d6d6d6';
    const color = chip.classList.contains('is-soldout') ? `color-mix(in srgb, ${fam} 45%, #0f0d0d)` : fam;
    const from = st.box;
    const moving = from && !same(from, to) && !still.matches && Element.prototype.animate;
    let frames = null;
    if (moving) frames = liquid(from, to);
    chips.forEach((c) => {
      if (c.offsetParent === null) return;
      const cb = Object.assign(boxOf(c), { bl: c.clientLeft, bt: c.clientTop }), w = windowOf(c);
      w.getAnimations().forEach((a) => a.cancel());
      w.style.background = color;
      Object.assign(w.style, frameIn(to, cb), { opacity: '1' });
      if (frames) w.animate(frames.map(([r, o], k) => frameIn(r, cb, k === 0 ? { offset: o, background: st.color || color } : k === frames.length - 1 ? { offset: o, background: color } : { offset: o })), { duration: 640, easing: 'linear' });
      else if (!st.chip && !still.matches && w.animate) w.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 200, easing: 'ease-out' });
    });
    st.chip = chip; st.box = to; st.color = color;
  }
  // 2.43 third pass, Dan: the blob is for the scent categories only; scent and size chips keep their plain fill.
  // The motion is a travelling drop: its centre eases from chip to chip with a small lean-back at the start and a
  // small overshoot at the end (ease-in-out-back), it stretches longer and thinner mid-flight (up to ~110 px longer,
  // ~25 % thinner) and is back to the chip's own shape as it lands. The colour blends old -> new across the move.
  // 640 ms, 24 samples, played linearly.
  function liquid(from, to) {
    const c1 = 0.5, c2 = c1 * 1.525;
    const eib = (t) => (t < 0.5 ? (Math.pow(2 * t, 2) * ((c2 + 1) * 2 * t - c2)) / 2 : (Math.pow(2 * t - 2, 2) * ((c2 + 1) * (t * 2 - 2) + c2) + 2) / 2);
    const lerp = (a, b, t) => a + (b - a) * t;
    const fc = { x: from.x + from.w / 2, y: from.y + from.h / 2 }, tc = { x: to.x + to.w / 2, y: to.y + to.h / 2 };
    const dist = Math.hypot(tc.x - fc.x, tc.y - fc.y);
    const extra = Math.min(dist * 0.6, 110);
    const out = []; const N = 24;
    for (let i = 0; i <= N; i++) {
      const t = i / N, p = eib(t), bulge = Math.pow(Math.sin(Math.PI * t), 1.2);
      const w = lerp(from.w, to.w, Math.min(1, Math.max(0, p))) + extra * bulge;
      const h = lerp(from.h, to.h, t) * (1 - 0.25 * bulge * Math.min(1, extra / 110));
      const cx = lerp(fc.x, tc.x, p), cy = lerp(fc.y, tc.y, p);
      out.push([{ x: cx - w / 2, y: cy - h / 2, w, h }, t]);
    }
    return out;
  }
  function syncBlobs() {
    syncBlob(famRow, '.aml-chip--family[aria-pressed="true"]:not(.aml-hidden)');
  }
  let blobRaf = 0;
  window.addEventListener('resize', () => { cancelAnimationFrame(blobRaf); blobRaf = requestAnimationFrame(() => { if (!blobs.size) return; blobs.forEach((st) => { st.box = null; }); syncBlobs(); }); });
  // 2.43 final (Dan, 2026-11-04: "get rid of the blob animation"): the blob is switched off; chips fill as they did.
  // liquid()/syncBlob() are left in place, unused, in case it comes back.
  function render() { renderCore(); }
  function renderCore() {
    const cur = currentScent(); const curFam = (variants.find((v) => scentOf(v) === cur) || {}).family;
    // 2.43, Dan: "clicking onto 14oz should only display the two scents that are available". When the chosen size is one
    // that only some scents come in, the family filter steps aside and the picker shows just those scents.
    const curSize = sizeRow ? currentSize() : null;
    const inSize = (scent) => variants.some((x) => scentOf(x) === scent && sizeOf(x) === curSize);
    const sizeOnly = !!sizeRow && [...seen].some((sc) => !inSize(sc));
    wrap.classList.toggle('is-size-only', sizeOnly);
    famRow.hidden = sizeOnly; allBtn.hidden = sizeOnly;
    wrap.classList.toggle('is-all', activeFamily === 'all' && !sizeOnly);
    allBtn.setAttribute('aria-pressed', String(activeFamily === 'all'));
    famRow.querySelectorAll('.aml-chip--family').forEach((b) => { b.setAttribute('aria-pressed', String(b.dataset.family === activeFamily)); b.classList.toggle('is-current', b.dataset.family === curFam && activeFamily !== curFam); });
    scentRow.querySelectorAll('.aml-chip--scent').forEach((l) => { const show = sizeOnly ? inSize(l.dataset.scent) : (activeFamily === 'all' || l.dataset.family === activeFamily); l.classList.toggle('aml-hidden', !show); const on = l.dataset.scent === cur; l.classList.toggle('is-selected', on); l.querySelector('input').checked = on; });
    if (!sizeRow) return;
    // 2.42: with size first, every size the product has is always offered (the row hides only for a one-size product).
    // A size is sold out when no scent has it in stock, or when the current scent has it and it is sold out.
    const allSizes = [...sizeSelect.options].map((o) => o.value).filter((sz) => variants.some((v) => sizeOf(v) === sz));
    sizeLabel.hidden = sizeRow.hidden = allSizes.length < 2;
    sizeRow.querySelectorAll('.aml-chip--size').forEach((l) => {
      const size = l.dataset.size; const v = variants.find((x) => scentOf(x) === cur && sizeOf(x) === size);
      l.classList.toggle('aml-hidden', !allSizes.includes(size));
      const sold = v ? v.available === false : !variants.some((x) => sizeOf(x) === size && x.available !== false);
      l.classList.toggle('is-soldout', sold);
      if (sold) l.setAttribute('aria-label', size + ', sold out'); else l.removeAttribute('aria-label');
      const on = !!v && size === curSize; l.classList.toggle('is-selected', on); l.querySelector('input').checked = on;
    });
    // Scents that do not come in the chosen size stay pickable (picking one moves the size to one it has) but step back.
    scentRow.querySelectorAll('.aml-chip--scent').forEach((l) => {
      const other = !variants.some((x) => scentOf(x) === l.dataset.scent && sizeOf(x) === curSize);
      l.classList.toggle('is-othersize', other);
      if (other && !l.classList.contains('is-soldout')) l.setAttribute('aria-label', l.dataset.scent + ', not in ' + curSize); else if (!l.classList.contains('is-soldout')) l.removeAttribute('aria-label');
    });
  }
  const host = optionSelect.closest('.product-details__options') || optionSelect.parentNode;
  host.parentNode.insertBefore(wrap, host); host.classList.add('aml-picker__native');
  optionSelect.addEventListener('change', () => { urlUsed = true; setTimeout(render, 0); });
  if (sizeSelect) sizeSelect.addEventListener('change', () => { urlUsed = true; setTimeout(render, 0); });
  const gal = document.querySelector('[data-product-gallery]'); if (gal && 'MutationObserver' in window) new MutationObserver(() => { urlUsed = true; render(); }).observe(gal, { attributes: true, subtree: true, attributeFilter: ['data-product-gallery-selected'] });
  const m = master(); if (m && 'MutationObserver' in window) new MutationObserver(() => render()).observe(m, { attributes: true, childList: true, subtree: true });
  { const f = (variants.find((v) => scentOf(v) === currentScent()) || {}).family; if (f && families.includes(f)) activeFamily = f; }
  render();
})();

/* Candle PDP (2.13): scent atmosphere. The page takes on a faint photographic ground made from the selected
   scent's material shot - a band across the top, behind everything, at 45 %. It crossfades when the customer
   picks another scent and drifts slower than the page as it scrolls. The band, the 45 % and the protective
   scrim are in aml-product.css; the first image is painted server-side by snippets/aml-scent-ground.liquid,
   so a ?variant= deep link is already right at first paint and never flashes another scent. */
(function () {
  const ground = document.querySelector('[data-aml-ground]');
  const dataEl = document.querySelector('[data-aml-scent-data]');
  if (!ground || !dataEl) return;                                                // no ground emitted: nothing to do
  let data; try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
  const layers = [].slice.call(ground.querySelectorAll('[data-aml-ground-layer]'));
  if (layers.length < 2) return;
  const famMap = data.bgFamily || {};
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const wide = window.matchMedia('(min-width: 768px)');

  // Resolution order, first hit wins: the variant's own shot, its family's default, then nothing at all.
  const bgOf = (id) => { const v = data.variants[id]; if (!v) return null; return v.bg || (v.family && famMap[v.family]) || null; };

  /* Swap: two stacked layers, the incoming one crossfades in, the outgoing one drops its image once it
     has faded so only one background is ever painted. Background only - nothing here can move the page. */
  let front = layers[1].classList.contains('is-on') ? 1 : 0;
  let shown = ground.getAttribute('data-aml-ground-variant') || null;
  // 2.17: the picture on screen, so 8 oz -> 14 oz of one scent (same shot) does not crossfade the page to itself.
  let shownImg = shown && bgOf(shown) ? bgOf(shown).d : null;

  function retire(el) {
    clearTimeout(el.amlRetire);
    el.amlRetire = setTimeout(() => {
      if (el.classList.contains('is-on')) return;
      el.style.removeProperty('--aml-bg-d');
      el.style.removeProperty('--aml-bg-m');
    }, reduce ? 0 : 280);
  }

  function show(bg) {
    const outgoing = layers[front];
    const incoming = layers[front === 0 ? 1 : 0];
    if (bg) {
      incoming.style.setProperty('--aml-bg-d', 'url("' + bg.d + '")');
      incoming.style.setProperty('--aml-bg-m', 'url("' + bg.m + '")');
    }
    outgoing.classList.remove('is-on');
    incoming.classList.toggle('is-on', !!bg);
    ground.classList.toggle('is-on', !!bg);                                      // the scrim goes with the image
    front = front === 0 ? 1 : 0;
    retire(outgoing);
  }

  document.addEventListener('aml:variant', (e) => {
    const id = e.detail && e.detail.id;
    if (!id || id === shown) return;                                             // includes the first sync after load
    shown = id;
    const bg = bgOf(id), img = bg ? bg.d : null;
    if (img === shownImg) return;                                                // same picture: nothing to fade
    shownImg = img;
    show(bg);
  });

  /* Opal's lazy-image watcher never sees a CSS background, so nothing is fetched for us: the selected scent
     is preloaded from the head, and the rest are warmed one at a time on the first hover or focus of a chip. */
  const seen = Object.create(null);
  function warm(bg) {
    if (!bg) return;
    const url = wide.matches ? bg.d : bg.m;
    if (!url || seen[url]) return;
    seen[url] = true;
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
  }
  const chips = document.querySelector('.aml-picker__scents');
  if (chips) {
    const hint = (ev) => {
      const chip = ev.target && ev.target.closest ? ev.target.closest('[data-scent]') : null;
      if (!chip) return;
      const scent = chip.getAttribute('data-scent');
      const hit = Object.keys(data.variants).find((k) => (data.variants[k].options || []).indexOf(scent) > -1);
      if (hit) warm(bgOf(hit));
    };
    chips.addEventListener('mouseover', hint);                                   // pointerenter does not bubble
    chips.addEventListener('focusin', hint);
  }

  /* Parallax: transform only (background-attachment: fixed is unreliable on iOS Safari), one rAF per
     scroll burst off a passive scroll listener. The band is keyed to the measured top of the "you might
     also like" recommendations section (Dan: "image should scroll until just above the line"), so it
     survives a page of a different length: the ground is exactly that tall and fades out over its last
     stretch. Off entirely under prefers-reduced-motion.

     2.14 (2026-09-12), the blur fix: the layer used to be as tall as the whole band and was pushed DOWN
     by 30 % of the scroll, so the photograph had to cover ~2100 px of page - a 4.14x upscale in device
     pixels on a Pixel 7, which is what Dan saw as blur. The layer is now one viewport tall plus a drift
     allowance (--aml-drift, read back from the CSS so the two cannot disagree) and this follows the
     scroll instead: it tracks the viewport while the band lasts, minus a drift that grows to --aml-drift
     across the band. The layer's top therefore sits at -0 -> -drift px in the viewport - the ground still
     moves against the page, and its last pixel still lands exactly on the band's bottom edge, where the
     mask has already faded it out just above the recommendations line.
     The trade-off, stated plainly: the old ground scrolled at 70 % of page speed (1435 px of apparent
     movement over the band), the new one at ~95 % (170-260 px). A photograph cannot be both viewport-sized
     and slower-than-page over 2100 px of scroll - that is exactly the geometry that forced the upscale.

     position: sticky was the first thing tried and cannot work here: .aml-ground is overflow: hidden,
     which makes it a scroll container, and a sticky child sticks inside THAT scrollport - which never
     scrolls - so it never pins. Dropping the overflow is worse: the band's bottom fade is a mask, and a
     mask's gradient tiles rather than clipping, so content below the band would not be cut off. */
  const REC = '[id^="shopify-section"][id*="product-recommendations"]';
  const drift = () => parseFloat(getComputedStyle(ground).getPropertyValue('--aml-drift')) || 0;

  /* Where the band starts: the top of <main>, i.e. the bottom of the whole header block
     (announcement bar + sticky icon strip + logo band). 2.14, Dan: "the bg photo shows
     through the header on mobile" - Opal's icon strip is sticky with a transparent
     background, so a band anchored at page 0 bled through it while the logo band below
     stayed solid, and the header read as broken in two. Measured rather than assumed,
     because the header is a different height on every breakpoint. */
  /* 2.34, Dan: "pdp bread crumbs shouldn't live on top of the bg mood photo" - where Opal shows its
     breadcrumb line (576 px and up) the band starts under it, so the trail sits on the plain page. */
  const CRUMB_GAP = 14;
  function headTop() {
    const main = document.querySelector('main');
    if (!main) return 0;
    const y = window.pageYOffset || 0;
    const crumbs = main.querySelector('.breadcrumbs');
    if (crumbs && crumbs.offsetParent !== null && crumbs.offsetHeight > 0) return Math.max(0, Math.round(crumbs.getBoundingClientRect().bottom + y + CRUMB_GAP));
    return Math.max(0, Math.round(main.getBoundingClientRect().top + y));
  }

  /* ...and where it ends. 2.35, Dan: "have it so that the mood background photo ends when you get to details and
     specifications. Everything underneath that should be black." So the band now stops at the top of the
     details & specifications accordion (.aml-acc), with a short fade (--aml-ground-fade in the CSS) that is
     finished by that line; "you might also like" stays the fallback for a page without the accordion. */
  /* 2.41, Dan: "not sure why they're so short on the desktop". In Opal's two columns the accordion sits high in the
     right-hand column, so the band ended level with it while the photographs on the left ran on below onto black.
     On desktop the band now runs to whichever is lower: the accordion, or the bottom of the gallery and buy module
     (.product__wrapper). Phones keep the 2.35 rule, since there the accordion is already below the gallery. */
  const TWO_COL = window.matchMedia('(min-width: 960px)');
  function measure() {
    const t = headTop();
    const y = window.pageYOffset || 0;
    const rec = document.querySelector('.aml-acc') || document.querySelector(REC);
    if (!rec) return { top: t, band: ground.offsetHeight };                      // no recommendations: keep the CSS fallback
    let end = Math.round(rec.getBoundingClientRect().top + y);
    const wrap = TWO_COL.matches && document.querySelector('.product__wrapper');
    if (wrap) end = Math.max(end, Math.round(wrap.getBoundingClientRect().bottom + y));
    const h = end - t;
    // 2.43, Dan: "limit the mood photo to the left, so the product card section is just black". In two columns the band
    // stops halfway across the gap between the gallery and the buy module.
    let w = 0;
    const gal = TWO_COL.matches && document.querySelector('.product__wrapper .product-gallery');
    const det = TWO_COL.matches && document.querySelector('.product__wrapper .product-details');
    if (gal && det) w = Math.round((gal.getBoundingClientRect().right + det.getBoundingClientRect().left) / 2);
    return { top: t, band: h > 400 ? h : ground.offsetHeight, w };
  }

  let top = 0;
  let band = ground.offsetHeight;
  let onScroll = null;

  function apply(m) {
    top = m.top;
    band = m.band;
    ground.style.setProperty('--aml-ground-top', top + 'px');
    ground.style.setProperty('--aml-ground-h', band + 'px');
    if (m.w) ground.style.setProperty('--aml-ground-w', m.w + 'px'); else ground.style.removeProperty('--aml-ground-w');
  }

  apply(measure());

  /* Reflow tracking. Opening the details accordion adds ~395px to the page and pushes the
     recommendations line down with it; without this the band kept its old height and the
     fade ended short of the line. Watching <main> rather than <body>: the band is absolutely
     positioned, so it cannot feed its own height back in and loop.

     2.17 (Dan: "parallax scroll is a little buggy at times, especially with details &
     specifications open"): the accordion animates its own height over 300 ms, so the
     ResizeObserver fires on every frame of that animation. The band used to re-measure a
     frame later in a rAF and then glide to each new height over 360 ms (.is-eased) - a glide
     restarted on every frame, so the band's real height trailed the content by up to ~190 px
     while the photograph was already positioned for the new height. Measured on the preview:
     the image slid ~500 px in 0.4 s against a 5 px/frame scroll. Now the observer applies the
     new height in the same frame the content moved (ResizeObserver runs after layout, before
     paint), and there is no glide: the band follows the accordion's own smooth animation, so
     the band, its fade and the photograph all use one height on every frame. resize/load keep
     the rAF-coalesced path. */
  const sync = () => {
    const m = measure();
    if (m.top === top && Math.abs(m.band - band) < 1) return;
    apply(m);
    if (onScroll) onScroll();
  };
  let rQueued = false;
  const remeasure = () => {
    if (rQueued) return;
    rQueued = true;
    requestAnimationFrame(() => { rQueued = false; sync(); });
  };

  const mainEl = document.querySelector('main');
  if (mainEl && typeof ResizeObserver === 'function') new ResizeObserver(sync).observe(mainEl);
  window.addEventListener('resize', remeasure, { passive: true });
  window.addEventListener('load', remeasure);                                    // images can shift the section down

  /* 2.17, the scroll-lag fix. The layer has to follow the viewport at ~75-100 % of the scroll,
     and it was moved by a transform written from a scroll listener on the main thread. Browsers
     scroll the page on a separate compositor thread, so every drawn frame showed the page at its
     new position and the photograph where the PREVIOUS frame left it: measured from screencast
     frames on the preview, the image sat one frame's scroll behind - ~6-7 px on a slow scroll,
     37 px (desktop) / 44 px (Pixel 7) on a fast one, flipping direction when the scroll did.
     Where the browser supports scroll-driven animations, the same movement is now a CSS
     animation on a view() timeline of the band (.is-timeline in aml-product.css), which the
     compositor runs in step with the scroll. Its keyframes give exactly the old formula
     (checked at nine scroll positions, 0.0 px apart), so the ground's geometry is unchanged.
     Elsewhere the old listener below still drives it. */
  const timeline = typeof CSS !== 'undefined' && CSS.supports &&
    CSS.supports('animation-timeline: view()') && CSS.supports('animation-range: contain 0% contain 100%');

  if (!reduce && timeline) {
    ground.classList.add('is-drift', 'is-timeline');                             // CSS drives the layer from here
  } else if (!reduce) {
    ground.classList.add('is-drift');                                            // switches the layer to viewport height
    let queued = false;
    let live = null;
    const frame = () => {
      queued = false;
      const y = window.pageYOffset || document.documentElement.scrollTop || 0;
      const near = y < top + band + 200;
      if (near !== live) { live = near; ground.classList.toggle('is-live', near); }
      if (!near) return;
      const d = drift();
      /* Scroll over which the band is still on screen. At the end of it the layer's
         bottom edge lands exactly on the band's bottom edge, so the image runs out
         where the mask has already faded it - never a hard edge part-way up. */
      const range = Math.max(1, band - window.innerHeight);
      const y0 = Math.max(0, y - top);
      const p = Math.min(1, y0 / range);
      const shift = 'translate3d(0,' + (Math.min(y0, range) - d * p).toFixed(1) + 'px,0)';
      layers[0].style.transform = shift;
      layers[1].style.transform = shift;
    };
    onScroll = () => { if (!queued) { queued = true; requestAnimationFrame(frame); } };
    window.addEventListener('scroll', onScroll, { passive: true });
    frame();
  }
})();

/* Candle/product PDP (2.14, Dan): the Shop Pay "Pay over time for orders over $35.00"
   line belongs next to the buy action, not up in the price block 770px above it. Opal
   renders it inside .product-details__price-container, in a [data-payment-terms-reference]
   wrapper; this moves that wrapper whole to just above Add to Cart, so nothing empty is
   left behind and the gap under the price closes by itself.

   The wrapper, not the <shopify-payment-terms> element: the element is a custom element
   with its own shadow DOM that re-renders from the surrounding <form>'s variant input, and
   both source and destination are inside form.product-details, so it keeps updating when
   the scent changes. Opal's own PaymentTerms helper looks for a [data-payment-terms-target]
   that this theme does not have, so it returns early and nothing else is moving this. */
(function () {
  const area = document.querySelector('.product-details__add-to-cart-area');
  const buttons = area && area.querySelector('.product-details__add-to-cart-buttons');
  if (!area || !buttons) return;

  function place() {
    const wrap = document.querySelector('[data-payment-terms-reference]');
    if (!wrap || wrap.parentElement === area) return !!wrap;
    if (!wrap.querySelector('shopify-payment-terms')) return false;              // Shopify has not filled it in yet
    area.insertBefore(wrap, buttons);
    return true;
  }

  if (place()) return;
  /* The element is injected by Shopify's script, so wait for it rather than polling forever. */
  const mo = new MutationObserver(() => { if (place()) mo.disconnect(); });
  mo.observe(document.body, { childList: true, subtree: true });
  setTimeout(() => mo.disconnect(), 10000);
})();


/* Polaris short-list grid (2.16): promote the deferred images when the grid comes near.
   The ten posters are 498 KB and sit ~1800px down the page; `loading="lazy"` did not hold them
   back (measured: all ten arrived before any scroll), so the snippet ships them as data-src and
   this brings them in 400px ahead of the viewport. No IntersectionObserver -> load them at once,
   which is the old behaviour rather than a broken grid. */
(function () {
  const imgs = document.querySelectorAll('img[data-aml-defer]');
  if (!imgs.length) return;
  const load = (img) => {
    if (img.dataset.amlDeferDone) return;
    img.dataset.amlDeferDone = '1';
    if (img.dataset.sizes) img.sizes = img.dataset.sizes;
    if (img.dataset.srcset) img.srcset = img.dataset.srcset;
    if (img.dataset.src) img.src = img.dataset.src;
    img.addEventListener('load', () => img.classList.add('is-loaded'), { once: true });
  };
  if (!('IntersectionObserver' in window)) { imgs.forEach(load); return; }
  const io = new IntersectionObserver((entries, obs) => {
    entries.forEach((e) => { if (e.isIntersecting) { load(e.target); obs.unobserve(e.target); } });
  }, { rootMargin: '400px 0px' });
  imgs.forEach((img) => io.observe(img));
})();


/* ==========================================================================
   2.22 "Checkout Fix" (2026-09-15). The "check out" button in Opal's
   added-to-cart message.

   Opal writes the message with its own form for the button:
     <form action="/cart" method="POST"><button name="checkout">check out</button></form>
   which, posted to /cart, would send the shopper to checkout. But the message
   sits inside the product's own <form action="/cart/add">, and a form cannot
   nest inside a form, so the browser drops Opal's inner form when the message
   is inserted. The button then submits the PRODUCT form: the item is added a
   second time and Shopify lands on /cart with quantity 2 (verified live on
   posters and candles, desktop and phone). Send the click to checkout instead.
   ========================================================================== */
(function () {
  'use strict';

  document.addEventListener('click', function (event) {
    const button = event.target.closest('[data-response-message] button[name="checkout"]');
    if (!button) return;
    const form = button.closest('form');
    // Only when the button has been swallowed by an add-to-cart form.
    if (!form || !/\/cart\/add/.test(form.getAttribute('action') || '')) return;
    event.preventDefault();
    event.stopPropagation();
    window.location.assign('/checkout');
  }, true);
})();

/* Candle PDP (2.34): "more from the apothecary". sections/aml-house-recs.liquid renders a card for every scent
   on sale and shows the first few; on a scent change this re-picks them with the same ordering as that Liquid
   (same scent family first, then the rest in product order, never the selected scent). */
(function () {
  const list = document.querySelector('[data-aml-recs]');
  const dataEl = document.querySelector('[data-aml-scent-data]');
  if (!list || !dataEl) return;
  let data; try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
  const limit = Number(list.getAttribute('data-limit')) || 4;
  const cards = [].slice.call(list.querySelectorAll('[data-aml-rec]'));
  document.addEventListener('aml:variant', (e) => {
    const v = data.variants[e.detail.id]; if (!v) return;
    const family = (v.family || '').trim();
    const isCurrent = (c) => (v.options || [v.title]).indexOf(c.dataset.scent) !== -1;
    const same = (c) => family && c.dataset.family === family;
    const queue = cards.filter((c) => !isCurrent(c)).sort((a, b) => (same(b) - same(a)) || (Number(a.dataset.i) - Number(b.dataset.i)));
    cards.forEach((c) => { const rank = queue.indexOf(c); c.style.order = rank < 0 ? 999 : rank; c.hidden = rank < 0 || rank >= limit; });
  });
})();

/* 2.35: price on the subtitle's line, to the right (Dan: "ideate whether or not it would be better if we put the price
   on the same line as the candles and then put it to the right"). On when <html data-aml-price="inline"> (set by
   snippets/aml-assets.liquid from the theme setting or the ?price= preview switch). The price element itself is moved,
   not copied, so Opal keeps updating it on every variant change. */
(function () {
  if (document.documentElement.getAttribute('data-aml-price') !== 'inline') return;
  const sub = document.querySelector('.product-details .aml-subtitle');
  const price = document.querySelector('.product-details .product-details__price-container');
  if (!sub || !price) return;
  const row = document.createElement('div'); row.className = 'aml-titleline';
  sub.parentNode.insertBefore(row, sub); row.appendChild(sub); row.appendChild(price);
})();

/* 2.43 (Dan, 2026-11-04): "add to cart button should have some sort of visual feedback that you successfully added".
   Opal adds over ajax and, on success, writes a message with a "check out" button into [data-response-message]. When
   that happens the add to cart button (and the sticky bar's copy of it) reads "added" with a tick for 1.8 s. */
(function () {
  const BTN = '.product-details__add-to-cart-button, .aml-buybar__button';
  let armed = 0;
  document.addEventListener('click', (e) => { if (e.target.closest('[data-product-atc], ' + BTN)) armed = Date.now(); }, true);
  let timer = 0;
  function flash() {
    const btns = document.querySelectorAll(BTN);
    btns.forEach((b) => { b.classList.remove('aml-atc--added'); b.style.setProperty('--aml-atc-fg', getComputedStyle(b).color); void b.offsetWidth; b.classList.add('aml-atc--added'); });
    clearTimeout(timer);
    timer = setTimeout(() => btns.forEach((b) => b.classList.remove('aml-atc--added')), 2200);
  }
  new MutationObserver((ms) => {
    if (!armed || Date.now() - armed > 15000) return;
    for (const m of ms) {
      const el = m.target && m.target.nodeType === 1 ? m.target.closest('[data-response-message]') : null;
      if (el && el.querySelector('button[name="checkout"]')) { armed = 0; flash(); return; }
    }
  }).observe(document.body, { childList: true, subtree: true });
})();

/* 2.43 (Dan): "quantity and in stock should be on the same line". Opal renders them as stacked siblings (one
   inventory-status per variant, only the selected one shown), so both move into one flex row, in place. */
(function () {
  const qty = document.querySelector('.product-details .product-quantity');
  if (!qty || qty.parentNode.classList.contains('aml-qty-row')) return;
  const stats = [...qty.parentNode.children].filter((el) => el.matches('[data-inventory-status]'));
  if (!stats.length) return;
  const row = document.createElement('div'); row.className = 'aml-qty-row';
  qty.parentNode.insertBefore(row, qty); row.appendChild(qty); stats.forEach((el) => row.appendChild(el));
})();
